import type { Middleware } from '@reduxjs/toolkit';
import { NEW_NOTIFICATION } from '@reduxLocal/actions/types';

const MAX_STRING_LENGTH = 10000; // Limit individual strings to 10KB
const MAX_PAYLOAD_SIZE = 50000; // Limit total payload to ~50KB

/**
 * Strips HTML tags from a string.
 */
function stripHTML(str: string): string {
	return str.replace(/<[^>]*>/g, '');
}

/**
 * Recursively cleans and truncates strings in an object.
 */
function sanitizeValue(value: any): any {
	if (typeof value === 'string') {
		let cleaned = stripHTML(value);
		if (cleaned.length > MAX_STRING_LENGTH) {
			cleaned = cleaned.slice(0, MAX_STRING_LENGTH) + '... [truncated]';
		}
		return cleaned;
	}

	if (Array.isArray(value)) {
		return value.map(sanitizeValue);
	}

	if (value !== null && typeof value === 'object') {
		const sanitized: any = {};
		for (const key in value) {
			if (Object.prototype.hasOwnProperty.call(value, key)) {
				sanitized[key] = sanitizeValue(value[key]);
			}
		}
		return sanitized;
	}

	return value;
}

/**
 * Middleware that ensures notifications and other large payloads are "clean" and limited in size.
 * This prevents QuotaExceededError in Redux Persist (localStorage).
 */
export const stateSanitizerMiddleware: Middleware = (store) => (next) => (action: any) => {
	if (!action || typeof action !== 'object' || typeof action.type !== 'string') {
		return next(action);
	}

	// We always want to sanitize NEW_NOTIFICATION as it's a known source of bloat
	// For other actions, we still perform basic string cleaning if they have a payload
	const isNotification = action.type === NEW_NOTIFICATION;

	if (action.payload && typeof action.payload === 'object') {
		try {
			// 1. Sanitize the payload (strip HTML, truncate strings)
			// For notifications we are more aggressive with HTML stripping
			const sanitizedPayload = sanitizeValue(action.payload);

			// 2. Check total size of the sanitized payload
			const payloadString = JSON.stringify(sanitizedPayload);
			if (payloadString.length > MAX_PAYLOAD_SIZE) {
				if (isNotification) {
					console.warn(`[stateSanitizerMiddleware] Dropping oversized notification payload (${payloadString.length} chars)`);
					action.payload = {
						...sanitizedPayload,
						_truncated_oversized: true,
						_original_size: payloadString.length,
						body: (sanitizedPayload.body || sanitizedPayload.message || '').slice(0, 1000),
						message: (sanitizedPayload.message || '').slice(0, 1000),
						fullDocument: undefined,
						data: undefined,
					};
				} else if (payloadString.length > MAX_PAYLOAD_SIZE * 2) {
					// For non-notifications, only warn and truncate very large payloads
					console.warn(`[stateSanitizerMiddleware] Large action payload detected: ${action.type} (${payloadString.length} chars)`);
				}
			} else {
				action.payload = sanitizedPayload;
			}
		} catch (e) {
			console.error('[stateSanitizerMiddleware] Failed to sanitize action payload:', e);
		}
	}

	return next(action);
};
